import Layout from "../layouts/Layout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { Calendar, MapPin } from "lucide-react"
import { ReactNode } from "react"
import { Head } from "@inertiajs/react"

const About = () => {
  const technologies = {
    "Backend": ["Ruby on Rails", "Ruby", "Golang", "Java", "Erlang", "PostgreSQL", "Redis", "Sidekiq", "GraphQL"],
    "Frontend": ["Ruby on Rails", "Hotwire", "JavaScript", "React", "TailwindCSS", "HTML5", "CSS3"],
    "AI/ML": [
      "Python",
      "OpenAI API",
      "LangChain",
      "AI Agents",
      "Prompt Engineering",
      "Vector Databases",
      "LLM Integration",
      "FastAPI",
      "Claude Code",
      "MCP",
      "Context Engineering",
      "Agent Orchestration"
    ],
    "DevOps": ["Docker", "Kubernetes", "AWS", "GitHub Actions", "Nginx", "Linux"],
    "Domain Industry": ["DNS", "WHOIS", "EPP", "Registry Operations", "ICANN Standards", "DNSSEC"]
  }

  const values = [
    {
      title: "Code Quality",
      description: "Clean, maintainable, and well-tested code that stands the test of time. Every line is written with future maintainers in mind."
    },
    {
      title: "Continuous Learning",
      description: "Staying current with technology trends and continuously improving skills to deliver cutting-edge solutions."
    },
    {
      title: "Problem Solving",
      description: "Approaching complex challenges with analytical thinking and creative solutions that deliver real business value."
    },
    {
      title: "AI Innovation",
      description: "Strategic integration of AI technologies to solve real business problems, not just for the sake of using AI."
    }
  ]

  return (
    <>
      <Head title="Codeware - About" >
        <meta name="description" content="About Codeware, our journey, and our technical expertise in Rails development and AI solutions." />
      </Head>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-6 rounded-full overflow-hidden ring-2 ring-primary/20">
            <img
              src="me1.jpeg"
              alt="Portrait of Sergey Tsyganov"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">About</h1>
          <p className="text-xl text-muted-foreground mb-8">
            My name is Sergey Tsyganov, and I am the founder and engineer behind Codeware—an independent software consultancy
            delivering <a href="https://rubyonrails.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Ruby on Rails</a> and 
            AI solutions for web applications. I specialize in web application development and AI integration,
            helping teams design robust architectures, optimize performance, and ship features with confidence from new projects
            to modernization of existing systems. When you work with Codeware, you work directly with me.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-muted-foreground">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>Estonia</span>
            </div>
            <div className="flex items-center gap-2">
              <a href="https://github.com/maricavor" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                <span className="sr-only">@maricavor</span>
                <svg className="h-5 w-5 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fill-rule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clip-rule="evenodd" />
                </svg>
                <span>@maricavor</span>
              </a>
            </div>
            <div className="flex items-center gap-2">
              <a href="https://www.linkedin.com/in/sergey-tsyganov" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                <span className="sr-only">Sergey Tsyganov</span>
                <svg className="h-5 w-5 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clip-rule="evenodd" />
                </svg>
                <span>Sergey Tsyganov</span>
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>10+ years Rails experience</span>
            </div>
          </div>
        </div>

        {/* Solopreneurship Section */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardHeader>
                <CardTitle className="text-2xl">Approach</CardTitle>
                <CardDescription className="text-base">
                  How I deliver exceptional results through focused expertise and personalized service
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Direct communication and collaboration with me, the developer</li>
                  <li>Agile project management tailored to each client's needs</li>
                  <li>Deep technical expertise without the overhead of a large team</li>
                  <li>Competitive pricing while maintaining premium quality</li>
                  <li>Flexibility to take on both small prototypes and larger applications</li>
                  <li>My personal investment in every project's success</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Technical Stack */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Technical Stack</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(technologies).map(([category, techs]) => (
              <Card key={category}>
                <CardHeader>
                  <CardTitle className="text-lg">{category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {techs.map((tech) => (
                      <Badge key={tech} variant="secondary" className="text-xs">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Values & Approach */}
        <section>
          <h2 className="text-3xl font-bold text-center mb-12">Values & Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value) => (
              <Card key={value.title}>
                <CardHeader>
                  <CardTitle className="text-lg">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </>
  )
}

About.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default About